import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, b2bApi, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { Stat, StatusPill, Card, fmtDate, fmtDateTime, asList } from "@/components/common/Stat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Layers, CreditCard, Building2 } from "lucide-react";
import { LicensePanel } from "@/pages/admin_hr/Billing";

export default function OwnerOrgDetail() {
  const t = useT();
  const { id } = useParams();
  const qc = useQueryClient();
  const overview = useQuery({ queryKey: ["owner-org-overview", id], queryFn: async () => (await api.get(`/platform/organizations/${id}/overview`)).data, retry: false });
  const license = useQuery({ queryKey: ["owner-org-license", id], queryFn: async () => (await api.get(`/platform/organizations/${id}/license`)).data, retry: false });
  const members = useQuery({ queryKey: ["owner-org-members", id], queryFn: async () => (await api.get(`/platform/organizations/${id}/members`)).data, retry: false });
  const audit = useQuery({ queryKey: ["owner-org-audit", id], queryFn: async () => (await b2bApi.get(`/b2b/org/${id}/audit`, { params: { limit: 20 } })).data, retry: false });
  const [limit, setLimit] = useState("");
  const [busy, setBusy] = useState(false);

  const o = overview.data?.organization || overview.data?.org || overview.data || {};
  const lic = license.data || {};
  const mlist = asList(members.data, "members", "items", "people");

  const saveLimit = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/organizations/${id}/license`, { limit: Number(limit) });
      toast.success(t("common.saved")); setLimit(""); qc.invalidateQueries({ queryKey: ["owner-org-license", id] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/platform/organizations/${id}/status`, { status }); toast.success(t("common.saved")); qc.invalidateQueries({ queryKey: ["owner-org-overview", id] }); qc.invalidateQueries({ queryKey: ["owner-orgs"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={t("b2b.owner.orgDetail")} title={o.name || id} subtitle={`${o.organization_type || o.type || "business"} · ${t("b2b.owner.created")} ${fmtDate(o.created_at)}`}
        actions={<div className="flex items-center gap-2">
          <StatusPill status={o.status || "active"} testId="owner-org-status" />
          {String(o.status || "active") === "active"
            ? <Button variant="outline" className="rounded-full" data-testid="owner-org-suspend" onClick={() => setStatus("suspended")}>Suspend</Button>
            : <Button className="rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="owner-org-activate" onClick={() => setStatus("active")}>Activate</Button>}
        </div>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label={t("b2b.owner.members")} value={overview.data?.member_count ?? overview.data?.members ?? mlist.length} testId="owner-org-stat-members" />
        <Stat icon={Layers} label={t("b2b.owner.seatLimit")} value={lic.limit ?? lic.seat_limit} sub={lic.used != null ? `${lic.used} used` : undefined} testId="owner-org-stat-limit" />
        <Stat icon={Building2} label={t("nav.teams")} value={overview.data?.team_count ?? overview.data?.teams} testId="owner-org-stat-teams" />
        <Stat icon={CreditCard} label={t("nav.invites")} value={overview.data?.pending_invites ?? overview.data?.invites} testId="owner-org-stat-invites" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={t("b2b.owner.seatLimit")} testId="owner-org-license-card">
          <p className="text-sm text-ink-500 mb-3">Seat ceiling used by the Inner Compass invite flow. Synced automatically from the Stripe licence when active; adjust manually for pilots or negotiated contracts.</p>
          <div className="flex gap-2">
            <Input type="number" min={1} value={limit} onChange={(e) => setLimit(e.target.value)} placeholder={String(lic.limit ?? "")} className="h-11 rounded-xl bg-white max-w-[160px]" data-testid="owner-org-limit-input" />
            <Button onClick={saveLimit} disabled={busy || !limit} className="rounded-full bg-sage-500 hover:bg-sage-600 text-white h-11" data-testid="owner-org-limit-save">{t("b2b.owner.updateLimit")}</Button>
          </div>
        </Card>
        <LicensePanel orgId={id} compact />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={t("b2b.owner.members")} testId="owner-org-members">
          <div className="divide-y divide-border max-h-80 overflow-auto">
            {mlist.map((m) => (
              <div key={m.user_id || m.id || m.email} className="py-2 text-sm flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="text-ink-900 truncate">{m.name || m.email}</div><div className="text-xs text-ink-500 truncate">{m.email}</div></div>
                <span className="text-xs text-ink-500">{m.role || m.rbac_role || "employee"}</span>
              </div>
            ))}
            {!mlist.length && <div className="text-sm text-ink-500 py-3">{members.isLoading ? t("common.loading") : t("common.empty")}</div>}
          </div>
          <Link to={`/admin/users`} className="text-sm text-sage-600 font-semibold mt-3 inline-block" data-testid="owner-org-manage-people">{t("nav.people")} →</Link>
        </Card>
        <Card title={t("b2b.owner.auditTitle")} testId="owner-org-audit">
          <div className="divide-y divide-border max-h-80 overflow-auto">
            {(audit.data?.items || []).map((a) => (
              <div key={a.id} className="py-2 text-sm flex justify-between gap-3"><span className="font-mono text-xs text-ink-900">{a.action}</span><span className="text-xs text-ink-500">{fmtDateTime(a.created_at)}</span></div>
            ))}
            {!(audit.data?.items || []).length && <div className="text-sm text-ink-500 py-3">{t("common.empty")}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
